import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // IMPORTED useNavigate
import SEO from '../../../component/common/SEO';
import { pageSEO } from '../../../config/seoConfig';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useLoader } from '../../../base/context/LoaderProvider';
import { submitCantonFairLead } from '../../../service/website/cantonFairLeadApiService';
import { emailRegex } from '../../../helper/regex';
import './style.css';

/** Keep phone-like input (+ and digits), max 16 chars including +. */
function sanitizePhoneInput(raw) {
  let v = String(raw ?? "").replace(/[^\d+]/g, "");
  v = v.replace(/(?!^)\+/g, "");
  return v.slice(0, 16);
}

const CantonFairForm = () => {
  const location = useLocation();
  const navigate = useNavigate(); // INITIALIZED navigate
  const { showToast } = useMessage();
  const { showLoading } = useLoader();

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkDeepLoginStatus = () => {
      if (typeof window === 'undefined') return false;

      // 🚨 OVERRIDE: If you know your exact Local Storage key, put it here.
      // Example: const exactKey = localStorage.getItem('importerr_session');
      const exactKey = localStorage.getItem('YOUR_EXACT_KEY_HERE'); 
      if (exactKey && exactKey !== 'null' && exactKey !== 'undefined') return true;

      try {
        const authKeywords = ['token', 'auth', 'user', 'session', 'jwt', 'login', 'profile', 'importerr'];
        
        // 1. Deep Scan Local Storage
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && authKeywords.some(keyword => key.toLowerCase().includes(keyword))) {
            const value = localStorage.getItem(key);
            if (value && value !== 'null' && value !== 'undefined' && value !== '{}' && value !== '""' && value !== 'false') {
              return true;
            }
          }
        }

        // 2. Deep Scan Session Storage
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && authKeywords.some(keyword => key.toLowerCase().includes(keyword))) {
             const value = sessionStorage.getItem(key);
             if (value && value !== 'null' && value !== 'undefined' && value !== 'false') {
               return true;
             }
          }
        }

        // 3. Deep Scan Cookies
        if (document.cookie && authKeywords.some(keyword => document.cookie.toLowerCase().includes(keyword))) {
          return true;
        }
      } catch (error) {
        console.warn("Storage check blocked or failed", error);
      }
      
      return false;
    };

    setIsAuthenticated(checkDeepLoginStatus());
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitBanner, setSubmitBanner] = useState(null);
  const [formData, setFormData] = useState({
    purposes: [], 
    otherPurposeDetails: '', 
    fullName: '',
    companyName: '',
    mobile: '',
    email: '',
    city: '',
    state: '',
    country: 'India',
    heardAbout: '',
    categories: [],
    platforms: [],
    step3BrandName: '', 
    expansionGoal: '',  
    expectedSku: '', 
    moq: '',
    currentSource: '',
    budget: '', 
    challenge: '',
    resellerInterest: '',
  });

  const purposeOptions = [
    { id: 'Reseller / Distributor', icon: '🛒', desc: 'Buy & resell on Amazon, Flipkart, Meesho etc.' },
    { id: 'Brand / D2C Seller', icon: '🏷️', desc: 'Source for my own brand' },
    { id: 'Offline Retailer', icon: '🏪', desc: 'Physical store / shop owner' },
    { id: 'Manufacturer / OEM', icon: '🏭', desc: 'Need components or finished goods' },
    { id: 'Bulk Importer', icon: '📦', desc: 'Import large quantities to India' },
    { id: 'Dropshipper', icon: '🚀', desc: 'Sell online without holding stock' },
    { id: 'Corporate Buyer', icon: '🏢', desc: 'Procurement for company use' },
    { id: 'Other', icon: '💡', desc: 'Something else entirely' },
  ];

  const categories = [
    'Electronics & Gadgets', 'Home & Kitchen', 'Fashion & Apparel', 'Toys & Games',
    'Beauty & Personal Care', 'Tools & Hardware', 'Sports & Fitness', 'Furniture & Decor',
    'Automotive Accessories', 'Stationery & Office', 'Baby Products', 'Pet Products',
    'Lighting & Electrical', 'Packaging & Supplies', 'Industrial Equipment', 'Other'
  ];

  const platforms = [
    'Amazon India', 'Flipkart', 'Meesho', 'Myntra', 'Nykaa', 'AJIO', 'JioMart',
    'Snapdeal', 'My Own Website/Shopify', 'Offline Only', 'ONDC'
  ];

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleArraySelection = (field, value) => {
    setFormData((prev) => {
      const array = prev[field];
      if (array.includes(value)) {
        return { ...prev, [field]: array.filter((item) => item !== value) };
      } else {
        return { ...prev, [field]: [...array, value] };
      }
    });
  };

  const handleSaveLead = async () => {
    if (formData.email && !emailRegex.test(formData.email)) {
      setSubmitBanner({ ok: false, text: "Please enter a valid email address." });
      showToast("Please enter a valid email address.", ToastTypes.WARNING);
      return;
    }

    const pathSeg = location.pathname.split("/").filter(Boolean).pop() || "canton2026";
    const campaignSlug = /^[a-z0-9-]+$/i.test(pathSeg) ? pathSeg.toLowerCase() : "canton2026";
    const mobile = sanitizePhoneInput(formData.mobile);
    if (!/^\+?[1-9]\d{6,14}$/.test(mobile)) {
      setSubmitBanner({ ok: false, text: "Please enter a valid Indian or international mobile number." });
      showToast("Please enter a valid Indian or international mobile number.", ToastTypes.WARNING);
      return;
    }

    setSubmitBanner(null);
    showLoading(true);
    try {
      const res = await submitCantonFairLead({ ...formData, mobile, campaignSlug });
      if (res?.status?.code === 200) {
        setSubmitBanner({ ok: true, text: "Thank you — your details were saved. Redirecting..." });
        showToast("Lead saved successfully!", ToastTypes.SUCCESS);
        
        // REDIRECT TO HOME PAGE AFTER A SHORT DELAY
        setTimeout(() => {
          navigate('/');
        }, 1500);

      } else {
        const msg = res?.status?.message || res?.error?.message || "Could not save lead. Please check your details and try again.";
        setSubmitBanner({ ok: false, text: msg });
        showToast(msg, ToastTypes.WARNING);
      }
    } catch (error) {
      const dup = error?.status === 406 || error?.data?.status?.code === 406;
      const msg =
        (dup && error?.data?.status?.message) ||
        error?.data?.status?.message ||
        error?.message ||
        "Failed to save lead. Please try again.";
      setSubmitBanner({ ok: false, text: msg });
      showToast(msg, dup ? ToastTypes.WARNING : ToastTypes.ERROR);
    } finally {
      showLoading(false);
    }
  };

  const renderProgressBar = () => {
    const steps = ['Purpose', 'Details', 'Sourcing', 'Confirm'];
    return (
      <div className="cf-progress-bar">
        {steps.map((label, index) => {
          const stepNum = index + 1;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;
          return (
            <React.Fragment key={label}>
              <div className={`cf-progress-step ${isActive ? 'cf-active' : ''} ${isCompleted ? 'cf-completed' : ''}`}>
                <div className="cf-step-circle">
                  {isCompleted ? '✓' : stepNum}
                </div>
                <span className="cf-step-label">{label}</span>
              </div>
              {/* Render the line as a flex peer, except after the last step */}
              {index < steps.length - 1 && (
                <div className={`cf-step-line ${isCompleted ? 'cf-line-completed' : ''}`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <SEO {...pageSEO.cantonFair} />
      <div className="cf-form-wrapper">
        {renderProgressBar()}

        <div className="cf-main-card">
          {/* STEP 1: PURPOSE */}
          {currentStep === 1 && (
            <div className="cf-step-content cf-fade-in">
              <h1 className="cf-title">Welcome to <span className="cf-highlight">Canton Fair</span></h1>
              <p className="cf-subtitle">Let us know how we can connect you with China's best suppliers through Importerr.com</p>

              <label className="cf-section-lbl">WHAT BRINGS YOU HERE? <span className="cf-required">*</span></label>
              <p className="cf-hint-text">Select all that apply</p>
              
              <div className="cf-purpose-grid">
                {purposeOptions.map((p) => {
                  const isSelected = formData.purposes.includes(p.id);
                  return (
                    <div 
                      key={p.id} 
                      className={`cf-purpose-card ${isSelected ? 'cf-selected' : ''}`}
                      onClick={() => toggleArraySelection('purposes', p.id)}
                    >
                      <div className="cf-purpose-icon">{p.icon}</div>
                      <div className="cf-purpose-info">
                        <h4>{p.id}</h4>
                        <p>{p.desc}</p>
                      </div>
                      <div className={`cf-radio-indicator ${isSelected ? 'cf-checked' : ''}`}>
                        {isSelected && <span className="cf-check-icon">✓</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* OTHER PURPOSE TEXT FIELD */}
              {formData.purposes.includes('Other') && (
                <div className="cf-input-full cf-fade-in" style={{ marginTop: '20px' }}>
                  <label className="cf-input-lbl">PLEASE SPECIFY YOUR PURPOSE <span className="cf-required">*</span></label>
                  <input 
                    type="text" 
                    className="cf-input" 
                    name="otherPurposeDetails" 
                    value={formData.otherPurposeDetails} 
                    onChange={handleInputChange} 
                    placeholder="Tell us what you are looking for..." 
                  />
                </div>
              )}
              
              <div className="cf-btn-row cf-right-align">
                <button 
                  className="cf-btn-primary" 
                  onClick={handleNext}
                  disabled={
                    formData.purposes.length === 0 || 
                    (formData.purposes.includes('Other') && !formData.otherPurposeDetails.trim())
                  }
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: DETAILS */}
          {currentStep === 2 && (
            <div className="cf-step-content cf-fade-in">
              <h2 className="cf-title cf-serif">Your Details</h2>
              <p className="cf-subtitle">We'll use this to follow up after the fair</p>

              <div className="cf-input-row">
                <div className="cf-input-col">
                  <label className="cf-input-lbl">FULL NAME <span className="cf-required">*</span></label>
                  <input type="text" className="cf-input" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="John Doe" />
                </div>
                <div className="cf-input-col">
                  <label className="cf-input-lbl">COMPANY / BRAND NAME</label>
                  <input type="text" className="cf-input" name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="Your Company" />
                </div>
              </div>

              <div className="cf-input-row">
                <div className="cf-input-col">
                  <label className="cf-input-lbl">MOBILE NUMBER <span className="cf-required">*</span></label>
                  <input
                    type="tel"
                    className="cf-input"
                    name="mobile"
                    inputMode="tel"
                    autoComplete="tel"
                    value={formData.mobile}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, mobile: sanitizePhoneInput(e.target.value) }))
                    }
                    placeholder="+919876543210 or +14155552671"
                  />
                </div>
                <div className="cf-input-col">
                  <label className="cf-input-lbl">EMAIL ADDRESS</label>
                  <input type="email" className="cf-input" name="email" value={formData.email} onChange={handleInputChange} placeholder="email@example.com" />
                </div>
              </div>

              <div className="cf-input-row">
                <div className="cf-input-col">
                  <label className="cf-input-lbl">CITY <span className="cf-required">*</span></label>
                  <input type="text" className="cf-input" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" />
                </div>
                <div className="cf-input-col">
                  <label className="cf-input-lbl">STATE</label>
                  <input type="text" className="cf-input" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" />
                </div>
              </div>

              <div className="cf-input-full">
                <label className="cf-input-lbl">COUNTRY <span className="cf-required">*</span></label>
                <input type="text" className="cf-input" name="country" value={formData.country} onChange={handleInputChange} placeholder="India" />
              </div>

              <div className="cf-input-full">
                <label className="cf-input-lbl">HOW DID YOU HEAR ABOUT IMPORTERR?</label>
                <select name="heardAbout" value={formData.heardAbout} onChange={handleInputChange} className="cf-select">
                  <option value="">Select one</option>
                  <option value="Canton Fair 2026">Canton Fair 2026</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Instagram/Social Media">Instagram/Social Media</option>
                  <option value="Google Search">Google Search</option>
                  <option value="Friend / Colleague">Friend / Colleague</option>
                  <option value="Jito/JBN Network">Jito/JBN Network</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="cf-btn-row cf-split">
                <button className="cf-btn-secondary" onClick={handleBack}>← Back</button>
                <button 
                  className="cf-btn-primary" 
                  onClick={handleNext}
                  disabled={!formData.fullName || !formData.mobile || !formData.city || !formData.country}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: SOURCING */}
          {currentStep === 3 && (
            <div className="cf-step-content cf-fade-in">
              <h2 className="cf-title cf-serif">Sourcing Needs</h2>
              <p className="cf-subtitle">Help us match you with the right Chinese suppliers</p>

              <div className="cf-input-full">
                <label className="cf-section-lbl">
                  PRODUCT CATEGORIES <span className="cf-required">*</span> <span style={{ textTransform: 'lowercase', color: 'var(--cf-text-muted)', fontSize: '13px', fontWeight: '500', letterSpacing: 'normal' }}>(can select multiple)</span>
                </label>
                <p className="cf-hint-text">What are you looking to source?</p>
                <div className="cf-pill-group">
                  {categories.map((cat) => (
                    <button 
                      key={cat} 
                      className={`cf-pill ${formData.categories.includes(cat) ? 'cf-pill-active' : ''}`}
                      onClick={() => toggleArraySelection('categories', cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="cf-input-full">
                <label className="cf-section-lbl">
                  WHERE DO YOU SELL / PLAN TO SELL? <span style={{ textTransform: 'lowercase', color: 'var(--cf-text-muted)', fontSize: '13px', fontWeight: '500', letterSpacing: 'normal' }}>(can select multiple)</span>
                </label>
                <div className="cf-pill-group">
                  {platforms.map((plat) => (
                    <button 
                      key={plat} 
                      className={`cf-pill ${formData.platforms.includes(plat) ? 'cf-pill-active' : ''}`}
                      onClick={() => toggleArraySelection('platforms', plat)}
                    >
                      {plat}
                    </button>
                  ))}
                </div>
              </div>

              {/* BRAND NAME SECTION (REQUIRED) */}
              <div className="cf-input-full">
                <label className="cf-input-lbl">BRAND NAME <span className="cf-required">*</span></label>
                <input 
                  type="text" 
                  className="cf-input" 
                  name="step3BrandName" 
                  value={formData.step3BrandName} 
                  onChange={handleInputChange} 
                  placeholder="Enter your brand name" 
                />
              </div>

              {/* CATEGORY EXPANSION QUESTION */}
              <div className="cf-input-full">
                <label className="cf-section-lbl">ARE YOU LOOKING TO START A NEW CATEGORY/PRODUCT OR EXPAND EXISTING CATEGORIES?</label>
                <div className="cf-pill-group">
                  {['Start a new category', 'Expand existing categories', 'Both'].map((opt) => (
                    <button 
                      key={opt}
                      className={`cf-pill ${formData.expansionGoal === opt ? 'cf-pill-active' : ''}`}
                      onClick={() => setFormData({...formData, expansionGoal: opt})}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="cf-input-row">
                <div className="cf-input-col">
                  <label className="cf-input-lbl">EXPECTED SKU</label>
                  <select name="expectedSku" value={formData.expectedSku} onChange={handleInputChange} className="cf-select">
                    <option value="">Select range</option>
                    <option value="0–10">0–10</option>
                    <option value="10–50">10–50</option>
                    <option value="50–100">50–100</option>
                    <option value="100–1000">100–1000</option>
                    <option value="More than 1000">More than 1000</option>
                  </select>
                </div>
                <div className="cf-input-col">
                  <label className="cf-input-lbl">EXPECTED MOQ PREFERENCE</label>
                  <select name="moq" value={formData.moq} onChange={handleInputChange} className="cf-select">
                    <option value="">Select</option>
                    <option value="1 piece (sample)">1 piece (sample)</option>
                    <option value="2-10 pieces">2-10 pieces</option>
                    <option value="10-50 pieces">10-50 pieces</option>
                    <option value="50-200 pieces">50-200 pieces</option>
                    <option value="200-500 pieces">200-500 pieces</option>
                    <option value="500+ pieces">500+ pieces</option>
                    <option value="Bulk / Container loads">Bulk / Container loads</option>
                  </select>
                </div>
              </div>

              <div className="cf-input-full">
                <label className="cf-input-lbl">CURRENTLY SOURCING FROM?</label>
                <select name="currentSource" value={formData.currentSource} onChange={handleInputChange} className="cf-select">
                  <option value="">Select</option>
                  <option value="Not sourcing yet — new to importing">Not sourcing yet — new to importing</option>
                  <option value="Alibaba directly">Alibaba directly</option>
                  <option value="Local Indian wholesaler">Local Indian wholesaler</option>
                  <option value="Existing China supplier">Existing China supplier</option>
                  <option value="Other import platforms">Other import platforms</option>
                </select>
              </div>

              {/* CONDITIONAL BUDGET SECTION */}
              {formData.currentSource !== 'Not sourcing yet — new to importing' && (
                <div className="cf-input-full cf-fade-in">
                  <label className="cf-input-lbl">MONTHLY SOURCING BUDGET</label>
                  <select name="budget" value={formData.budget} onChange={handleInputChange} className="cf-select">
                    <option value="">Select range</option>
                    <option value="Under ₹1 Lakh">Under ₹1 Lakh</option>
                    <option value="₹1-5 Lakh">₹1-5 Lakh</option>
                    <option value="₹5-20 Lakh">₹5-20 Lakh</option>
                    <option value="₹20-50 Lakh">₹20-50 Lakh</option>
                    <option value="₹50 Lakh - 1 Cr">₹50 Lakh - 1 Cr</option>
                    <option value="Above ₹1 Cr">Above ₹1 Cr</option>
                  </select>
                </div>
              )}

              <div className="cf-input-full">
                <label className="cf-input-lbl">BIGGEST SOURCING CHALLENGE?</label>
                <textarea 
                  className="cf-textarea"
                  name="challenge" 
                  value={formData.challenge} 
                  onChange={handleInputChange}
                  placeholder="e.g. Can't find low MOQ suppliers, quality issues, customs clearance, payments..."
                  rows="3"
                ></textarea>
              </div>

              <div className="cf-input-full">
                <label className="cf-section-lbl">INTERESTED IN IMPORTERR.COM'S E-COMMERCE RESELLER PROGRAM?</label>
                <div className="cf-pill-group">
                  {['Yes — tell me more', 'Maybe — need more info', 'Just exploring right now'].map((opt) => (
                    <button 
                      key={opt}
                      className={`cf-pill ${formData.resellerInterest === opt ? 'cf-pill-active' : ''}`}
                      onClick={() => setFormData({...formData, resellerInterest: opt})}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="cf-btn-row cf-split">
                <button className="cf-btn-secondary" onClick={handleBack}>← Back</button>
                <button 
                  className="cf-btn-primary" 
                  onClick={handleNext}
                  disabled={formData.categories.length === 0 || !formData.step3BrandName.trim()}
                >
                  Review →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: CONFIRM */}
          {currentStep === 4 && (
            <div className="cf-step-content cf-fade-in">
              <h2 className="cf-title cf-serif">Confirm Details</h2>
              <p className="cf-subtitle">Review before we save your information</p>

              <div className="cf-summary-box">
                <h3 className="cf-summary-hdr">👤 Contact</h3>
                <div className="cf-summary-grid">
                  <span className="cf-summary-key">Name</span><span className="cf-summary-val">{formData.fullName || '—'}</span>
                  <span className="cf-summary-key">Company</span><span className="cf-summary-val">{formData.companyName || '—'}</span>
                  <span className="cf-summary-key">Phone</span><span className="cf-summary-val">{formData.mobile || '—'}</span>
                  <span className="cf-summary-key">Email</span><span className="cf-summary-val">{formData.email || '—'}</span>
                  <span className="cf-summary-key">Location</span>
                  <span className="cf-summary-val">
                    {[formData.city, formData.state, formData.country].filter(Boolean).join(', ') || '—'}
                  </span>
                  <span className="cf-summary-key">Referred by</span><span className="cf-summary-val">{formData.heardAbout || '—'}</span>
                </div>
              </div>

              <div className="cf-summary-box">
                <h3 className="cf-summary-hdr">🎯 Purpose</h3>
                <div className="cf-summary-grid">
                  <span className="cf-summary-key">Type</span>
                  <span className="cf-summary-val">
                    {formData.purposes.map(p => p === 'Other' && formData.otherPurposeDetails ? `Other (${formData.otherPurposeDetails})` : p).join(', ') || '—'}
                  </span>
                </div>
              </div>

              <div className="cf-summary-box">
                <h3 className="cf-summary-hdr">📦 Sourcing</h3>
                <div className="cf-summary-grid">
                  <span className="cf-summary-key">Categories</span><span className="cf-summary-val">{formData.categories.join(', ') || '—'}</span>
                  <span className="cf-summary-key">Platforms</span><span className="cf-summary-val">{formData.platforms.join(', ') || '—'}</span>
                  <span className="cf-summary-key">Brand Name</span><span className="cf-summary-val">{formData.step3BrandName || '—'}</span>
                  <span className="cf-summary-key">Goal</span><span className="cf-summary-val">{formData.expansionGoal || '—'}</span>
                  <span className="cf-summary-key">Expected SKU</span><span className="cf-summary-val">{formData.expectedSku || '—'}</span>
                  <span className="cf-summary-key">MOQ</span><span className="cf-summary-val">{formData.moq || '—'}</span>
                  <span className="cf-summary-key">Current supplier</span><span className="cf-summary-val">{formData.currentSource || '—'}</span>
                  
                  {formData.currentSource !== 'Not sourcing yet — new to importing' && (
                    <>
                      <span className="cf-summary-key">Budget/month</span><span className="cf-summary-val">{formData.budget || '—'}</span>
                    </>
                  )}
                  
                  <span className="cf-summary-key">Challenges</span><span className="cf-summary-val">{formData.challenge || '—'}</span>
                  <span className="cf-summary-key">Importerr interest</span><span className="cf-summary-val">{formData.resellerInterest || '—'}</span>
                </div>
              </div>

              <div className="cf-btn-row cf-split">
                <button className="cf-btn-secondary" onClick={() => setCurrentStep(1)}>← Edit</button>
                <button className="cf-btn-primary" onClick={handleSaveLead}>✓ Submit</button>
              </div>
              {submitBanner && (
                <p
                  className="cf-submit-banner"
                  style={{
                    marginTop: 16,
                    padding: "12px 14px",
                    borderRadius: 8,
                    fontSize: 14,
                    background: submitBanner.ok ? "#ecfdf5" : "#fef2f2",
                    color: submitBanner.ok ? "#065f46" : "#991b1b",
                    border: `1px solid ${submitBanner.ok ? "#a7f3d0" : "#fecaca"}`,
                  }}
                  role="status"
                >
                  {submitBanner.text}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Removed completely from DOM if isAuthenticated is true */}
        {!isAuthenticated && (
          <div className="cf-form-footer cf-fade-in">
            Don't have an Importerr account yet? <a href="https://www.importerr.com/signup" target="_blank" rel="noopener noreferrer">Sign up here</a>
          </div>
        )}
      </div>
    </>
  );
};

export default CantonFairForm;